import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import axios from 'axios';

import HttpService from '../../services/HttpService';
import UserService from '../../services/UserService';
import { XtMotionStatus, XtMotionStatusDetails } from '../../utils/Enums';
import { FORMATS, FORMATS_PROFILE, FPS, MAX_DURATION, MAX_SIZE, RESOLUTION } from '../../utils/Env';
import { SignerURL } from '../../utils/Types';
import DragAndDropVideoFile from '../DragAndDropVideoFile';
import { confirm } from '@evs/react-components';

import styles from './XtramotionConverter.module.scss';

const reactmediafinfo = require('react-mediainfo');
const { getInfo } = reactmediafinfo;

export type XtramotionConverterParams = {
  contactOnClick: () => void;
  shareOnClick: () => void;
  learnOnClick: () => void;
};

const XtramotionConverter = (props: XtramotionConverterParams): JSX.Element => {
  const [file, setFile] = React.useState<File>();
  const [status, setStatus] = React.useState<XtMotionStatus>(XtMotionStatus.DEFAULT);
  const [error, setError] = React.useState<XtMotionStatusDetails>(XtMotionStatusDetails.DEFAULT);
  const [progress, setProgress] = React.useState<number>(0);
  const [objectSize, setObjectSize] = React.useState<number>(0);

  let objectType = '';

  const axiosClient = HttpService.getAxiosClient();
  const apiAddress = HttpService.getApiAddress();

  useEffect(() => {}, []);

  useEffect(() => {
    if (error === XtMotionStatusDetails.DEFAULT) {
      return;
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'video_upload',
        status: 'error',
        error_type: `${error}`,
      },
    });
  }, [error]);

  useEffect(() => {
    // During the upload of the file, send the information to the CRM
    if (status === XtMotionStatus.PROGRESS) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'video_upload',
          status: 'success',
        },
      });

      const user = UserService.getUserDetails();

      if (user.email !== 'xtramotiontester@gmail.com') {
        const data = {
          ...user,
          topic: 'XtraMotion self trial',
          sourceCampaign: 'XtraMotion evaluation platform',
          description:
            'Uploaded video on the XtraMotion evaluation platform in order to test the AI-based super-slow motion replay',
        };

        axiosClient.post(`${apiAddress}/xtramotion/leads`, data).then((res) => {});
      }
    }
  }, [status]);

  useEffect(() => {
    if (file) {
      setError(XtMotionStatusDetails.DEFAULT);
      setStatus(XtMotionStatus.ANALYZING);
      validateFile(file)
        .then(() => {
          setProgress(0);
          setStatus(XtMotionStatus.PROGRESS);
          sendFile(file);
        })
        .catch(() => {
          setStatus(XtMotionStatus.FAILURE);
        });
    }
  }, [file]);

  const validateFile = (file: File) => {
    return new Promise((resolve, reject) => {
      if (file.size > MAX_SIZE) {
        setError(XtMotionStatusDetails.SIZE);
        return reject(new Error(XtMotionStatusDetails.SIZE));
      }
      setObjectSize(file.size);

      getInfo(file).then((info: any) => {
        let videoAnalysis = false;
        let generalAnalysis = false;
        info.media.track.forEach((track: any) => {
          if (track['@type'] === 'General') {
            generalAnalysis = true;

            if (!(FORMATS.includes(track.Format) && FORMATS_PROFILE.includes(track.Format_Profile))) {
              setError(XtMotionStatusDetails.FORMAT);
              return reject(new Error(XtMotionStatusDetails.FORMAT));
            }
            // Save the object type for the content type of the http request
            if (file.type !== undefined && file.type !== '' && file.type !== null) {
              objectType = file.type;
            } else {
              objectType = `application/${track.Format.toLowerCase()}`;
            }

            if (parseInt(track.FileSize) > MAX_SIZE) {
              setError(XtMotionStatusDetails.SIZE);
              return reject(new Error(XtMotionStatusDetails.SIZE));
            }
          } else if (track['@type'] === 'Video') {
            videoAnalysis = true;
            if (process.env.REACT_APP_ENV !== 'production') {
              console.log(track);
            }

            if (parseInt(track.Duration) > MAX_DURATION) {
              setError(XtMotionStatusDetails.DURATION);
              return reject(new Error(XtMotionStatusDetails.DURATION));
            }

            if (!RESOLUTION.includes(`${track.Width}x${track.Height}`)) {
              setError(XtMotionStatusDetails.RESOLUTION);
              return reject(new Error(XtMotionStatusDetails.RESOLUTION));
            }

            if ('Rotation' in track && track.Rotation !== '0.000') {
              setError(XtMotionStatusDetails.ROTATION);
              return reject(new Error(XtMotionStatusDetails.ROTATION));
            }

            if (!FPS.includes(track.FrameRate.slice(0, -1))) {
              setError(XtMotionStatusDetails.FPS);
              return reject(new Error(XtMotionStatusDetails.FPS));
            } else if(parseInt(track.FrameRate.slice(0, -1)) < 50 && track.ScanType === 'Progressive') {
              confirm('Confirm', {
                title: 'Warning: low framerate detected',
                confirmation: 'Please be aware that the XtraMotion algorithm has been optimized for framerates equal to or greater than 50 frames per second. Below this framerate, the quality of the results may be impacted. If you are interested in workflows with lower framerates, feel free to contact us.',
                confirmButtonStyle: 'primary',
              });
            }
          }
        });

        if (!(generalAnalysis && videoAnalysis)) {
          setError(XtMotionStatusDetails.UNKNOWN);
          return reject(new Error(XtMotionStatusDetails.UNKNOWN));
        }

        return resolve(true);
      });
    });
  };

  const sendFile = (file: File | undefined) => {
    if (!file) {
      return;
    }

    axiosClient
      .post(`${apiAddress}/xtramotion/presigned`, { ObjectKey: file?.name, ObjectType: objectType })
      .then((res) => {
        const signedUrl: SignerURL = res.data; // { Url:""}

        if (signedUrl.url === '' || signedUrl.url === undefined || signedUrl.url === null) {
          throw new Error(`Wrong signed url received from server : ${signedUrl}`);
        }

        const user = UserService.getUserDetails();
        let userEmail = user.email;

        if (userEmail === '' || userEmail === undefined || userEmail === null) {
          userEmail = UserService.getUsername();
        }

        const options = {
          headers: {
            'Content-Type': objectType,
            'x-amz-tagging': `UserId=${userEmail}`,
            Accept: '*/*',
          },
          onUploadProgress: (progressEvent: { loaded: any }) => setProgress(parseInt(progressEvent.loaded)),
        };

        axios
          .put(signedUrl.url, file, options)
          .then(() => {
            setError(XtMotionStatusDetails.SUCCESS);
            setStatus(XtMotionStatus.SUCCESS);
          })
          .catch(() => {
            setError(XtMotionStatusDetails.UPLOAD);
            setStatus(XtMotionStatus.FAILURE);
          });
      })
      .catch(() => {
        setError(XtMotionStatusDetails.UPLOAD);
        setStatus(XtMotionStatus.FAILURE);
      });
    // send file with presigned url
  };

  const fileLoaded = (file: File) => {
    setFile(file);
  };

  const tryAgain = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'try_again',
      },
    });

    setError(XtMotionStatusDetails.DEFAULT);
    setStatus(XtMotionStatus.DEFAULT);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (status !== XtMotionStatus.PROGRESS) {
      const fileObject = e.dataTransfer?.files?.[0];

      if (!fileObject) {
        return;
      }

      setFile(fileObject);
    }
  };

  return (
    <div
      className={styles.container}
      onDrop={(e) => handleDrop(e)}
      onDragOver={(e) => handleDragOver(e)}
      onDragEnter={(e) => handleDragEnter(e)}
      onDragLeave={(e) => handleDragLeave(e)}
    >
      <div className={styles.headerTitle}>Welcome to the EVS XtraMotion trial platform</div>
      <div className={styles.header}>
        <span className={styles.headerTextPart1}>
          See for yourself how XtraMotion can generate super smooth, super slow-motion replays from any footage! The
          process couldn’t be easier.&nbsp;
        </span>
        <span className={styles.headerTextPart2}>
          Simply upload your chosen video clip and we’ll send you an email with the link to the XtraMotion processed
          video. If you like the result and want to use it on one of your productions – just get in touch with us.
        </span>
        <span className={styles.headerTextPart3}>
          <br />
          <br />
          The XtraMotion Trial Platform is at the disposal of EVS’ partners and (potential) customers for testing
          purposes only. Any use of the platform for commercial or private purposes is strictly forbidden.
        </span>
      </div>
      <DragAndDropVideoFile
        fileLoaded={fileLoaded}
        progress={progress}
        objectSize={objectSize}
        status={status}
        statusDetails={error}
        tryAgain={tryAgain}
      />
      <div className={styles.footer}>
        Supported input formats: 1080p/i | 720p/i
        <br />
        Supported input wrappers: MP4 | MOV | MXF-OP1A
        <br />
        Max duration: 10 seconds
        <br />
        Max size: 500 MB
      </div>
      {/* <div className={styles.actionMenu}>
        <div>
          <img alt="share" src={share} className={styles.circleButton} />
          Share the experience
        </div>
        <div>
          <img alt="learn" src={learn} className={styles.circleButton} />
          Learn more about XtraMotion
        </div>
        <div>
          <img alt="contact" src={contact} className={styles.circleButton} />
          Contact us
        </div>
      </div> */}
    </div>
  );
};

export default XtramotionConverter;
